import { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, BookOpenIcon } from '@heroicons/react/24/outline';

const userManualContent = `
<div id="estimated-revenue-per-manager">
<h2>Estimated Revenue per Manager Report</h2>

<p>The <strong>Estimated Revenue per Manager</strong> report provides comprehensive insights into sales performance by analyzing revenue data across different dimensions. This report helps you understand how individual managers are performing and identify trends in your business.</p>

<div id="report-structure">
<h3>Report Structure and Grouping</h3>

<p>The report organizes data using a hierarchical grouping structure:</p>

<ul>
<li><strong>Manager</strong>: Primary grouping by sales manager (field: <code>request_manager</code>)</li>
<li><strong>Client</strong>: Secondary grouping by client company (field: <code>client_name</code>)</li>
<li><strong>Hotel</strong>: Tertiary grouping by hotel/venue (field: <code>hotel_name</code>)</li>
</ul>

<p>This three-level hierarchy allows you to:</p>
<ul>
<li>Analyze performance at the manager level to see overall sales results</li>
<li>Drill down to client level to understand which clients generate the most revenue</li>
<li>Further drill down to hotel level to see specific venue performance</li>
<li>Identify patterns in manager-client-hotel relationships</li>
</ul>

<p>The grouping structure ensures that each row in the report represents a unique combination of Manager + Client + Hotel, with aggregated metrics for that specific combination.</p>

</div>

<div id="kpis">
<h3>Key Performance Indicators (KPIs)</h3>

<p>The report tracks several important metrics with specific field mappings:</p>

<h4>Revenue Metrics</h4>
<ul>
<li><strong>Estimated Revenue</strong>: Field <code>estimated_revenue</code> - The projected revenue based on current bookings and pricing</li>
<li><strong>Final Revenue</strong>: Field <code>realised_revenue</code> - The actual realized revenue from completed bookings</li>
<li><strong>Signed Revenue</strong>: Field <code>signed_revenue</code> - The confirmed revenue amount from signed contracts</li>
</ul>

<h4>Group Metrics</h4>
<ul>
<li><strong>Group Count</strong>: Field <code>group_count</code> - Number of groups managed by each manager</li>
<li><strong>Total Rooms</strong>: Field <code>total_rooms</code> - Total number of rooms across all groups</li>
</ul>

<h4>Status Distribution</h4>
<p>The report categorizes bookings by status using the <code>status_slug</code> field:</p>
<ul>
<li><strong>CTS</strong>: <code>cts</code> - Confirmed to Start (confirmed bookings)</li>
<li><strong>LOST</strong>: <code>lost</code> - Lost business opportunities</li>
<li><strong>BOOKED</strong>: <code>booked</code> - Successfully booked reservations</li>
<li><strong>OPEN</strong>: <code>open</code> - Open/pending bookings</li>
<li><strong>Reservation en cours</strong>: <code>reservation_en_cours</code> - Bookings in progress</li>
</ul>

<h4>Time-based Metrics</h4>
<ul>
<li><strong>Creation Period</strong>: Fields <code>request_year</code>, <code>request_month</code>, <code>request_quarter</code> - When the request was created</li>
<li><strong>Arrival Period</strong>: Fields <code>arrival_year</code>, <code>arrival_month</code>, <code>arrival_quarter</code> - When guests will arrive</li>
<li><strong>Request Date</strong>: Field <code>date_of_request</code> - Exact date when the request was made</li>
<li><strong>Last Updated</strong>: Field <code>last_updated</code> - When the record was last modified</li>
</ul>

</div>

<div id="revenue-calculations">
<h3>Revenue Calculations</h3>

<p>The report calculates revenue using the following logic:</p>

<ul>
<li><strong>Estimated Revenue</strong>: Based on the <code>estimated_revenue</code> field at the time of creation</li>
<li><strong>Final Revenue</strong>: Uses the <code>realised_revenue</code> field for actual realized amounts</li>
<li><strong>Revenue per Manager</strong>: Total revenue divided by the number of groups managed</li>
<li><strong>Revenue Growth</strong>: Period-over-period comparison using creation or arrival date filters</li>
<li><strong>Average Revenue per Group</strong>: Total estimated revenue divided by group count</li>
</ul>

</div>

</div>

<div id="filters-panel">
<h2>Filters Panel</h2>

<p>The Filters Panel provides powerful filtering capabilities to help you analyze your business data. The system uses <strong>cascading filters</strong> - smart filters that work together to show you exactly the data you need while preventing impossible combinations.</p>

<div id="cascading-filters-guide">
<h3>Cascading Filters Guide</h3>

<h4>What are Cascading Filters?</h4>

<p>Cascading filters are intelligent filters that work together to help you find exactly the data you need. When you select one filter, it automatically updates the available options in other filters to show only relevant choices. This prevents you from selecting combinations that would result in no data.</p>

<h4>How They Work</h4>

<p>Think of it like a funnel - each filter narrows down your data, and the next filter only shows options that are available in the already-filtered data.</p>

</div>

<div id="available-filters">
<h3>Available Filters</h3>

<h4>1. Date Filters</h4>
<ul>
<li><strong>Creation Years/Months/Quarters</strong>: When the request was created</li>
<li><strong>Arrival Years/Months/Quarters</strong>: When guests will arrive</li>
</ul>

<h4>2. Business Filters</h4>
<ul>
<li><strong>Managers</strong>: Sales managers responsible for the requests</li>
<li><strong>Segments</strong>: Type of business (e.g., "Ad-hoc Group", "Corporate")</li>
<li><strong>Clients</strong>: Company names</li>
<li><strong>Hotels</strong>: Venue names</li>
<li><strong>Hotel Groups</strong>: Hotel chain groupings</li>
</ul>

</div>

<div id="how-to-use-filters">
<h3>How to Use Filters</h3>

<h4>Example 1: Finding Data for a Specific Manager</h4>

<p><strong>Step 1</strong>: Select "Creation Year 2025"</p>
<ul>
<li>This shows all data from 2025</li>
</ul>

<p><strong>Step 2</strong>: Select "Arrival Year 2025"</p>
<ul>
<li>This narrows to requests created in 2025 for arrivals in 2025</li>
</ul>

<p><strong>Step 3</strong>: Select "Segment: Ad-hoc Group"</p>
<ul>
<li>This further narrows to only Ad-hoc Group business</li>
</ul>

<p><strong>Step 4</strong>: Select specific managers</p>
<ul>
<li>The manager dropdown now only shows managers who have Ad-hoc Group business in 2025</li>
<li>Other managers are disabled (grayed out) because they don't have relevant data</li>
</ul>

<p><strong>Result</strong>: You see only data for the selected managers who have Ad-hoc Group business in 2025.</p>

<h4>Example 2: Finding Data for Specific Hotels</h4>

<p><strong>Step 1</strong>: Select "Creation Year 2024"</p>
<p><strong>Step 2</strong>: Select "Manager: John Smith"</p>
<p><strong>Step 3</strong>: Select "Segment: Corporate"</p>

<p><strong>What happens</strong>: The hotel dropdown now only shows hotels where John Smith has Corporate business in 2024. Hotels where he doesn't have this type of business are disabled.</p>

<h4>Example 3: Finding Data for Specific Clients</h4>

<p><strong>Step 1</strong>: Select "Arrival Year 2025"</p>
<p><strong>Step 2</strong>: Select "Manager: Sarah Johnson"</p>
<p><strong>Step 3</strong>: Select "Hotel Group: Marriott"</p>

<p><strong>What happens</strong>: The client dropdown only shows clients where Sarah Johnson has Marriott business with 2025 arrivals. Other clients are disabled.</p>

</div>

<div id="key-behaviors">
<h3>Key Behaviors</h3>

<h4>✅ What Works Well</h4>
<ul>
<li><strong>Unselecting unwanted options</strong>: If you want to exclude specific managers, unselect them one by one</li>
<li><strong>Progressive filtering</strong>: Start broad and narrow down step by step</li>
<li><strong>Smart suggestions</strong>: The system shows you what's actually available</li>
</ul>

<h4>⚠️ What to Avoid</h4>
<ul>
<li><strong>Don't unselect everything and reselect</strong>: This can cause unexpected behavior</li>
<li><strong>Don't try to select disabled options</strong>: They're disabled because no data exists for that combination</li>
</ul>

</div>

<div id="practical-examples">
<h3>Practical Examples</h3>

<h4>Example A: Monthly Performance Review</h4>
<p><strong>Goal</strong>: See how Manager "Alice" performed in Q3 2024</p>

<p><strong>Steps</strong>:</p>
<ol>
<li>Select "Creation Year 2024"</li>
<li>Select "Creation Quarter 3" (July-September)</li>
<li>Select "Manager: Alice"</li>
<li>Select "Segment: Ad-hoc Group"</li>
</ol>

<p><strong>Result</strong>: You see Alice's Ad-hoc Group performance for Q3 2024 only.</p>

<h4>Example B: Hotel Performance Analysis</h4>
<p><strong>Goal</strong>: See which hotels performed well for Corporate business in 2025</p>

<p><strong>Steps</strong>:</p>
<ol>
<li>Select "Arrival Year 2025"</li>
<li>Select "Segment: Corporate"</li>
<li>Select "Hotel Group: Hilton"</li>
</ol>

<p><strong>Result</strong>: You see all Hilton hotels with Corporate business in 2025, with only relevant managers and clients available.</p>

### Example C: Client-Specific Analysis
**Goal**: Analyze performance for "ABC Company" across all their business

**Steps**:
1. Select "Client: ABC Company"
2. Select "Creation Year 2025"

**Result**: You see all ABC Company's business in 2025, with only relevant managers, segments, and hotels available.

</div>

<div id="tips-for-best-results">
<h3>Tips for Best Results</h3>

<ol>
<li><strong>Start with broad filters</strong>: Begin with years or segments</li>
<li><strong>Use the "Reset" button</strong>: If you get stuck, reset all filters and start over</li>
<li><strong>Check disabled options</strong>: If something is grayed out, it means no data exists for that combination</li>
<li><strong>Progressive narrowing</strong>: Add filters one at a time to see how they affect your data</li>
<li><strong>Use the summary</strong>: The "Total Discrepancies" shows how many data points match your criteria</li>
</ol>

</div>

<div id="troubleshooting">
<h3>Troubleshooting</h3>

<h4>Common Issues and Solutions</h4>

<p><strong>Problem</strong>: "I can't select the manager I want"</p>
<p><strong>Solution</strong>: The manager might not have data for your current filter combination. Try removing some filters first.</p>

<p><strong>Problem</strong>: "My numbers look wrong after filtering"</p>
<p><strong>Solution</strong>: Check if you accidentally unselected all managers and reselected them. Try using the "Reset" button and filtering by unselecting unwanted options instead.</p>

<p><strong>Problem</strong>: "I see no data"</p>
<p><strong>Solution</strong>: Your filter combination might be too restrictive. Try removing some filters to see more data.</p>

<p><strong>Problem</strong>: "Some options are grayed out"</p>
<p><strong>Solution</strong>: This is normal! Grayed out options don't have data for your current filter combination. The system is helping you avoid empty results.</p>

</div>

</div>

<hr>

<p>The cascading filter system is designed to help you find exactly the data you need while preventing impossible combinations. It's like having a smart assistant that knows your data and guides you to the right information!</p>
`;

interface UserManualModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function UserManualModal({ isOpen, onClose }: UserManualModalProps) {
  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-7xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                    <BookOpenIcon className="h-6 w-6 text-blue-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                      User Manual
                    </Dialog.Title>
                    <div className="flex h-[600px]">
                      {/* Table of Contents - Fixed Left Column */}
                      <div className="w-1/4 pr-4 border-r border-gray-200 overflow-y-auto">
                        <div className="sticky top-0">
                          <h4 className="font-semibold text-gray-900 mb-3">Table of Contents</h4>
                          <nav className="space-y-2">
                            <a href="#estimated-revenue-per-manager" className="block text-sm text-blue-600 hover:text-blue-800 hover:underline font-semibold">1. Estimated Revenue per Manager Report</a>
                            <div className="ml-4 space-y-1">
                              <a href="#report-structure" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">1.1 Report Structure and Grouping</a>
                              <a href="#kpis" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">1.2 Key Performance Indicators</a>
                              <a href="#revenue-calculations" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">1.3 Revenue Calculations</a>
                            </div>
                            <a href="#filters-panel" className="block text-sm text-blue-600 hover:text-blue-800 hover:underline font-semibold">2. Filters Panel</a>
                            <div className="ml-4 space-y-1">
                              <a href="#cascading-filters-guide" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.1 Cascading Filters Guide</a>
                              <a href="#available-filters" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.2 Available Filters</a>
                              <a href="#how-to-use-filters" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.3 How to Use Filters</a>
                              <a href="#practical-examples" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.4 Practical Examples</a>
                              <a href="#tips-for-best-results" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.5 Tips for Best Results</a>
                              <a href="#troubleshooting" className="block text-xs text-gray-600 hover:text-blue-800 hover:underline">2.6 Troubleshooting</a>
                            </div>
                          </nav>
                        </div>
                      </div>
                      
                      {/* Content - Right Column */}
                      <div className="w-3/4 pl-4 overflow-y-auto">
                        <div className="prose prose-gray prose-sm max-w-none [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:text-gray-900 [&_h2]:border-b [&_h2]:border-gray-200 [&_h2]:pb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-6 [&_h3]:mb-3 [&_h3]:text-gray-800 [&_p]:text-gray-700 [&_p]:mb-4 [&_p]:leading-relaxed [&_ul]:mb-4 [&_ul]:ml-4 [&_ul]:space-y-1 [&_ol]:mb-4 [&_ol]:ml-4 [&_ol]:space-y-1 [&_li]:text-gray-700 [&_li]:leading-relaxed [&_strong]:text-gray-900 [&_strong]:font-semibold [&_hr]:my-6 [&_hr]:border-gray-300">
                          <div dangerouslySetInnerHTML={{ __html: userManualContent }} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 sm:ml-3 sm:w-auto"
                    onClick={onClose}
                  >
                    Close
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
